import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import { DebtType } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { useMemo } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { formatSpacedNumber } from '../../../../../../../utils/format';
import type { Step7Data } from '../../../../FormData.dk.types';
import { radioOptions } from '../../../../config/globals';
import {
  debtCollectionMonthlyDebtPaymentFieldOptions,
  debtCollectionOutstandingAmountFieldOptions,
} from './config';

type DebtCollectionFormGroupProps = {
  onAdd?: (type?: DebtType) => void;
  onRemove?: (index: number, type?: DebtType) => void;
};
export function DebtCollectionFormGroup({
  onAdd,
  onRemove,
}: DebtCollectionFormGroupProps) {
  const { t } = useTranslation();

  const { watch } = useFormContext();

  const hasDebtCollection = watch('Frontend/HasDebtCollection') === 'Yes';

  const registerOptions: RegisterOptions = useMemo(
    () => ({
      required: true,
      onChange: (e) => {
        if (e.target.value === 'Yes') {
          onAdd?.(DebtType.Collection);
          return;
        }

        onRemove?.(0, DebtType.Collection);
      },
    }),
    [onAdd, onRemove]
  );

  return (
    <>
      <RadioFieldUI<Step7Data>
        name="Frontend/HasDebtCollection"
        label={t('Debt.Collection.question')}
        data-testid="has-collection-debt-radio"
        radioStyle="both"
        options={radioOptions}
        registerOptions={registerOptions}
      />

      {hasDebtCollection && (
        <>
          <Controller
            name={'LoanApplication/Debts.0.OutstandingAmount'}
            render={({ field: { name }, fieldState: { error } }) => (
              <NumberFieldUI<Step7Data>
                name={name}
                label={t('Debt.Collection.OutstandingAmount')}
                data-testid={'debt-collection-outstanding-amount-input'}
                error={error}
                format={formatSpacedNumber}
                suffix={t('suffix.currency')}
                {...debtCollectionOutstandingAmountFieldOptions}
              />
            )}
          />

          <Controller
            name={'LoanApplication/Debts.0.MonthlyDebtPayment'}
            render={({ field: { name }, fieldState: { error } }) => (
              <NumberFieldUI<Step7Data>
                name={name}
                label={t('Debt.Collection.MonthlyDebtPayment')}
                data-testid={'debt-collection-monthly-payment-input'}
                error={error}
                format={formatSpacedNumber}
                suffix={t('suffix.currency')}
                {...debtCollectionMonthlyDebtPaymentFieldOptions}
              />
            )}
          />
        </>
      )}
    </>
  );
}
